/**
 * Shared date range helpers used by the unified <DateRangeFilter /> component
 * and by any page that sends date parameters to the backend.
 *
 * Preset vocabulary accepted (intentionally matches every variant already used
 * across the Google Ads pages — don't rename without grepping call sites):
 *   today, yesterday, last7 / 7days, last30 / 30days, last90,
 *   thisMonth / this_month, lastMonth / last_month, all / all_time, custom
 */

/**
 * Format a Date as local YYYY-MM-DD. NEVER use toISOString().split('T')[0]
 * — that converts to UTC and off-by-one-days in TZ > UTC (Istanbul = UTC+3).
 */
export function formatDateYMD(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Compute { startDate, endDate } (Date objects) for a named preset.
 * Returns null for 'custom' (caller provides its own dates).
 * Returns { startDate: null, endDate: null } for 'all' / 'all_time'.
 */
export function computePresetRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const daysAgo = (n) => {
    const d = new Date(today);
    d.setDate(d.getDate() - n);
    return d;
  };

  switch (preset) {
    case 'today':
      return { startDate: today, endDate: today };

    case 'yesterday': {
      const y = daysAgo(1);
      return { startDate: y, endDate: y };
    }

    case 'last7':
    case '7days': {
      const end = daysAgo(1);
      const start = daysAgo(7);
      return { startDate: start, endDate: end };
    }

    case 'last30':
    case '30days': {
      const end = daysAgo(1);
      const start = daysAgo(30);
      return { startDate: start, endDate: end };
    }

    case 'last90': {
      const end = daysAgo(1);
      const start = daysAgo(90);
      return { startDate: start, endDate: end };
    }

    case 'thisMonth':
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: start, endDate: today };
    }

    case 'lastMonth':
    case 'last_month': {
      const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastOfPrev = new Date(firstThis);
      lastOfPrev.setDate(0);
      const firstOfPrev = new Date(lastOfPrev.getFullYear(), lastOfPrev.getMonth(), 1);
      return { startDate: firstOfPrev, endDate: lastOfPrev };
    }

    case 'all':
    case 'all_time':
      return { startDate: null, endDate: null };

    case 'custom':
      return null;

    default:
      return null;
  }
}

/**
 * Build the { dateRange, startDate, endDate } trio that most Glowytics API
 * endpoints expect. For named presets, only dateRange is needed — the backend
 * computes the window. For 'custom', the YMD strings are sent alongside.
 */
export function buildApiDateParams(dateRange, customStart, customEnd) {
  if (dateRange === 'custom') {
    return {
      dateRange: 'custom',
      startDate: formatDateYMD(customStart),
      endDate: formatDateYMD(customEnd),
    };
  }
  return { dateRange, startDate: null, endDate: null };
}

/**
 * Append the standard date params to a URLSearchParams object.
 * Convenience for the many pages that build query strings manually.
 */
export function appendDateParams(params, dateRange, customStart, customEnd) {
  const { dateRange: range, startDate, endDate } = buildApiDateParams(
    dateRange,
    customStart,
    customEnd
  );
  if (range) params.set('dateRange', range);
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  return params;
}

/** Human-readable labels (English). */
export const PRESET_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 Days',
  '7days': 'Last 7 Days',
  last30: 'Last 30 Days',
  '30days': 'Last 30 Days',
  last90: 'Last 90 Days',
  thisMonth: 'This Month',
  this_month: 'This Month',
  lastMonth: 'Last Month',
  last_month: 'Last Month',
  all: 'All Time',
  all_time: 'All Time',
  custom: 'Custom Range',
};

/** Default preset list for new pages. Ordered as shown in dropdown. */
export const DEFAULT_PRESETS = [
  'yesterday',
  'last7',
  'last30',
  'last90',
  'thisMonth',
  'lastMonth',
  'custom',
];

/**
 * Produce a short display string for the currently selected range —
 * e.g. "Last 30 Days" or "Mar 17 - Apr 17" for custom.
 */
export function describeRange(dateRange, customStart, customEnd) {
  if (dateRange === 'custom') {
    if (customStart && customEnd) {
      const fmt = (d) =>
        d instanceof Date
          ? d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
          : '';
      return `${fmt(customStart)} - ${fmt(customEnd)}`;
    }
    return PRESET_LABELS.custom;
  }
  return PRESET_LABELS[dateRange] || dateRange || '—';
}
